/* eslint-disable no-restricted-globals */
import $ from '../jQuery/jQuery';
import moduleRegistry from '../module-registry/module-registry';
import {
    generateId, getElementScrollHeight, isClickOrEnter, noop,
} from '../utils/utils';

const config = {
    openCloseDelayMs: 20,
    horizontalMediaQuery: window.matchMedia('(min-width: 992px)'),
    selectors: {
        module: '.rfct-accordion',
        itemHead: '.rfct-accordion__item-head',
        itemBody: '.rfct-accordion__item-body',
        itemBodyContent: '.rfct-accordion__item-body-content',
        controlButtonOpenAll: '.rfct-accordion__control-button--open-all',
        controlButtonCloseAll: '.rfct-accordion__control-button--close-all',
        horizontalTargetContainer: '.rfct-horizontal-accordion-target',
    },
    classNames: {
        itemHeadActive: 'rfct-accordion__item-head--active',
        controlButtonActive: 'rfct-accordion__control-button--active',
        multi: 'rfct-accordion--multi',
        horizontal: 'rfct-accordion--horizontal',
        horizontalTargetContainerActive: 'rfct-horizontal-accordion-target--active',
    },
};

export default class Accordion {
    constructor($rootElement, options, document) {
        this._$rootElement = $rootElement;
        this._elements = this._getDomElements();
        this._multi = this._$rootElement.hasClass(config.classNames.multi);
        this._horizontal = this._$rootElement.hasClass(config.classNames.horizontal);
        this._$horizontalTargetContainer = options && options.horizontalTargetContainerSelector
            ? $(document).find(options.horizontalTargetContainerSelector) : null;
    }

    _getDomElements() {
        return {
            $itemHeads: this._$rootElement.find(config.selectors.itemHead),
            $itemBodies: this._$rootElement.find(config.selectors.itemBody),
            $controlButtonOpenAll: this._$rootElement.find(config.selectors.controlButtonOpenAll),
            $controlButtonCloseAll: this._$rootElement.find(config.selectors.controlButtonCloseAll),
        };
    }

    static _mergeOptions(options) {
        if (!options) {
            return { ...config.defaultOptions };
        }

        return { ...config.defaultOptions, ...options };
    }

    init() {
        this._initAttributes();
        this._initControls();
        this._addEventListeners();
        this._initHorizontalTargetContainer();
    }

    _initAttributes() {
        this._elements.$itemHeads.each((i, itemHead) => {
            const $itemHead = $(itemHead);
            const $itemBody = Accordion._getItemBody($itemHead);
            const isOpen = Accordion._isOpen($itemHead);

            Accordion._assignIds($itemHead, $itemBody);
            Accordion._assignAriaRelationshipAttributes($itemHead, $itemBody);
            Accordion._assignAriaStateAttributes($itemHead, $itemBody, isOpen);
        });
    }

    /**
     * Assignes IDs to the given item head
     * and item body
     * @param {jQuery} $itemHead - The item head
     * @param {jQuery} $itemBody - The item body
     */
    static _assignIds($itemHead, $itemBody) {
        if (!$itemHead.attr('id')) {
            $itemHead.attr('id', generateId());
        }

        if (!$itemBody.attr('id')) {
            $itemBody.attr('id', generateId());
        }
    }

    /**
     * Assignes the ARIA relationship attributes
     * to the given item head and item body
     * @param {jQuery} $itemHead - The item head
     * @param {jQuery} $itemBody - The item body
     */
    static _assignAriaRelationshipAttributes($itemHead, $itemBody) {
        const itemHeadId = $itemHead.attr('id');
        const itemBodyId = $itemBody.attr('id');

        $itemHead.attr('aria-controls', itemBodyId); // the head controls its body
        $itemBody.attr('aria-labelledby', itemHeadId); // the body is labelled by the head
    }

    /**
     * Assignes the ARIA state attributes
     * to the given item head and item body
     * @param {jQuery} $itemHead - The item head
     * @param {jQuery} $itemBody - The item body
     * @param {boolean} isOpen - Boolean that indicates
     * if the item is currently open.
     */
    static _assignAriaStateAttributes($itemHead, $itemBody, isOpen) {
        $itemHead.attr('aria-selected', isOpen);
        $itemHead.attr('aria-expanded', isOpen);
        $itemBody.attr('aria-hidden', !isOpen);
    }

    _initControls() {
        this._updateControlButtonState();
    }

    _addEventListeners() {
        this._elements.$itemHeads.on('click keydown', (e) => {
            if (!isClickOrEnter(e)) {
                return;
            }

            const $itemHead = $(e.currentTarget);

            if (Accordion._isOpen($itemHead)) {
                // item is already open, so close it
                this._closeItem($itemHead);
                return;
            }

            if (this._multi) {
                this._openItem($itemHead);
            } else {
                this._closeAllItems(() => {
                    this._openItem($itemHead);
                });
            }
        });

        this._elements.$itemBodies.on('transitionend', (e) => {
            if (e.target !== e.currentTarget) {
                return;
            }

            const { originalEvent } = e;
            const transitionProperty = originalEvent.propertyName;

            if (transitionProperty !== 'height') {
                // ignore other transitions than `height`
                return;
            }

            this._updateControlButtonState();

            const $itemBody = $(e.currentTarget);
            const itemBodyScrollHeight = getElementScrollHeight($itemBody);
            const height = parseInt($itemBody.css('height'), 10);

            if (height === 0) {
                Accordion._setItemBodyVisibility($itemBody, false);
            }

            if (height === itemBodyScrollHeight) {
                // body is completly opened so now set it to auto height
                Accordion._setItemBodyHeight($itemBody, 'auto');
            }
        });

        this._elements.$controlButtonOpenAll.on('click', () => {
            this._openAllItems();
            this._updateControlButtonState();
        });

        this._elements.$controlButtonCloseAll.on('click', () => {
            this._closeAllItems();
            this._updateControlButtonState();
        });
    }

    /**
     * Initializes the horizontal
     * target container
     */
    _initHorizontalTargetContainer() {
        if (!this._horizontal
            || !this._$horizontalTargetContainer
            || !this._$horizontalTargetContainer.length
        ) {
            return;
        }

        this._elements.$itemHeads.each((i, el) => {
            const $itemHead = $(el);

            if (Accordion._isOpen($(el))) {
                const $itemBody = Accordion._getItemBody($itemHead);
                this._copyItemBodyContentToTargetContainer($itemBody);
                this._showTargetContainer();
                return false;
            }

            return true;
        });

        const addChangeListener = (fn) => (config.horizontalMediaQuery.addEventListener
            ? config.horizontalMediaQuery.addEventListener('change', fn)
            : config.horizontalMediaQuery.addListener(fn));

        addChangeListener(() => {
            if (config.horizontalMediaQuery.matches) {
                this._elements.$itemHeads.each((i, el) => {
                    const $itemHead = $(el);

                    if (Accordion._isOpen($(el))) {
                        const $itemBody = Accordion._getItemBody($itemHead);
                        this._copyItemBodyContentToTargetContainer($itemBody);
                        return false;
                    }

                    return true;
                });
            } else {
                this._elements.$itemHeads.each((i, el) => {
                    const $itemHead = $(el);

                    if (Accordion._isOpen($(el))) {
                        const $itemBody = Accordion._getItemBody($itemHead);
                        Accordion._setItemBodyHeight($itemBody, 'auto');
                        Accordion._setItemBodyVisibility($itemBody, true);
                    } else {
                        this._closeItem($itemHead);
                    }
                });
            }
        });
    }

    /**
     * Copies the content of the given $itemBody
     * to the target container
     * @param {jQuery} $itemBody - The given item body
     */
    _copyItemBodyContentToTargetContainer($itemBody) {
        const $itemBodyContent = Accordion._getItemBodyContent($itemBody);
        const html = $itemBodyContent.html();
        this._$horizontalTargetContainer.html(html);
    }

    /**
     * Shows the horizontal
     * target container
     */
    _showTargetContainer() {
        this._$horizontalTargetContainer
            .addClass(config.classNames.horizontalTargetContainerActive);
    }

    /**
     * Hides the horizontal
     * target container
     */
    _hideTargetContainer() {
        this._$horizontalTargetContainer
            .removeClass(config.classNames.horizontalTargetContainerActive);
    }

    /**
     * Clears the contents of the target container
     */
    _clearTargetContainer() {
        if (!this._$horizontalTargetContainer || !this._$horizontalTargetContainer.length) {
            return;
        }

        this._$horizontalTargetContainer.html('');
    }

    /**
     * Updates the state of the control
     * buttons (open all and close all buttons)
     */
    _updateControlButtonState() {
        if (this._hasOpenItem()) {
            this._elements.$controlButtonCloseAll.prop('disabled', false);
            this._elements.$controlButtonCloseAll
                .addClass(config.classNames.controlButtonActive);
        } else {
            this._elements.$controlButtonCloseAll.prop('disabled', true);
            this._elements.$controlButtonCloseAll
                .removeClass(config.classNames.controlButtonActive);
        }

        if (this._allItemsOpen()) {
            this._elements.$controlButtonOpenAll.prop('disabled', true);
            this._elements.$controlButtonOpenAll
                .removeClass(config.classNames.controlButtonActive);
        } else {
            this._elements.$controlButtonOpenAll.prop('disabled', false);
            this._elements.$controlButtonOpenAll
                .addClass(config.classNames.controlButtonActive);
        }
    }

    /**
     * Checks if any item is open
     * @returns Boolean
     */
    _hasOpenItem() {
        return this._elements.$itemHeads.hasClass(config.classNames.itemHeadActive);
    }

    /**
     * Checks if all items are open
     * @returns Boolean
     */
    _allItemsOpen() {
        return this._elements.$itemHeads.length === this._elements.$itemHeads.filter(`.${config.classNames.itemHeadActive}`).length;
    }

    /**
     * Checks if the item is open
     * by the given item head.
     * @param {jQuery} - The item head to check
     */
    static _isOpen($itemHead) {
        return $itemHead.hasClass(config.classNames.itemHeadActive);
    }

    /**
     * Opens all items
     * when multi mode
     * is enabled
     */
    _openAllItems() {
        if (!this._multi) {
            return;
        }

        this._elements.$itemHeads.each((i, el) => {
            this._openItem($(el));
        });
    }

    /**
     * Closes all items
     * @param {Function} - A callback function to be called when all items are closed
     * were closed.
     */
    _closeAllItems(callback = noop) {
        const itemHeadCount = this._elements.$itemHeads.length;
        let callbackCounter = 0;

        const closeCallback = () => {
            callbackCounter++;

            if (callbackCounter === itemHeadCount) {
                callback();
            }
        };

        this._elements.$itemHeads.each((i, el) => {
            this._closeItem($(el), closeCallback);
        });
    }

    /**
     * Opens an accordion item
     * by the given item head
     * @param {jQuery} $itemHead - The head of the item to open
     * @param {Function} callback - Callback function to be called when the item is opened
     * @returns {Promise} - A promise is returned which
     * resolves, after the item was opened.
     */
    _openItem($itemHead, callback = noop) {
        if (Accordion._isOpen($itemHead)) {
            // item is already open so there is nothing to do
            callback();
            return;
        }

        const $itemBody = Accordion._getItemBody($itemHead);

        if (this._horizontal
            && config.horizontalMediaQuery.matches
            && this._$horizontalTargetContainer
            && this._$horizontalTargetContainer.length
        ) {
            this._copyItemBodyContentToTargetContainer($itemBody);
            $itemHead.addClass(config.classNames.itemHeadActive);
            this._showTargetContainer();
            callback();
        } else {
            Accordion._assignAriaStateAttributes($itemHead, $itemBody, true);

            // when opening the accordion we have to handle
            // the case that there is no existing inline `height`
            // (when opening an accordion item the first time).
            // To get a smooth transition, 0 is set as initial
            // height before opening the item
            Accordion._setItemBodyHeight($itemBody, 0);
            Accordion._setItemBodyVisibility($itemBody, true);

            $itemHead.addClass(config.classNames.itemHeadActive);

            setTimeout(() => {
                Accordion._setItemBodyHeight($itemBody);
                callback();
            }, config.openCloseDelayMs);
        }
    }

    /**
     * Closes an item body by the given item head.
     * @param {jQuery} $itemHead - The head of the item to close
     * @param {Function} callback - Callback function to be called when the item is closed
     * @returns {Promise} - A promise is returned which
     * resolves, after the item was closed.
     */
    _closeItem($itemHead, callback = noop) {
        if (!Accordion._isOpen($itemHead)) {
            // item is not open so nothing is to do
            callback();
            return;
        }

        const $itemBody = Accordion._getItemBody($itemHead);

        if (this._horizontal
            && config.horizontalMediaQuery.matches
            && this._$horizontalTargetContainer
            && this._$horizontalTargetContainer.length
        ) {
            this._clearTargetContainer();
            $itemHead.removeClass(config.classNames.itemHeadActive);
            Accordion._setItemBodyHeight($itemBody, 0);
            Accordion._setItemBodyVisibility($itemBody, false);
            this._hideTargetContainer();
            callback();
        } else {
            Accordion._assignAriaStateAttributes($itemHead, $itemBody, false);

            // for the case that we first had the tabs (dektop view)
            // and then switched to mobile view and close
            // an accordion item we have to set the the scrollHeight
            // before closing the accordion item.
            // Otherwise the transition will not work, because
            // the body won't have a fixed height.
            // After a short timeout, the body is closed by
            // setting the height to 0.
            Accordion._setItemBodyHeight($itemBody);
            $itemHead.removeClass(config.classNames.itemHeadActive);

            setTimeout(() => {
                Accordion._setItemBodyHeight($itemBody, 0);
                callback();
            }, config.openCloseDelayMs);
        }
    }

    /**
     * Measures the scroll height
     * of the given item body
     * and sets it as `height`
     * @param {jQuery} $itemBody - The item body to size
     * @param {number | string} height - Optional height to use.
     * If not specified, the elements scrollHeight is used.
     */
    static _setItemBodyHeight($itemBody, height) {
        let resultHeight;

        if (height !== undefined) {
            resultHeight = height;
        } else {
            resultHeight = getElementScrollHeight($itemBody);
        }

        $itemBody.css('height', `${resultHeight}${typeof resultHeight === 'string' ? '' : 'px'}`);
    }

    /**
     * Shows or hides the given itemBody
     * @param {jQuery} $itemBody - The item body so show or hide
     * @param {boolean} visible - A boolean to control
     * if the itemBody should be shown or hidden
     */
    static _setItemBodyVisibility($itemBody, visible) {
        $itemBody.css('display', visible ? 'block' : 'none');
    }

    /**
     * Returns the item body for the given item head
     * @param {jQuery} $itemHead - The item head to
     * retreive the body from
     */
    static _getItemBody($itemHead) {
        return $itemHead.next();
    }

    /**
     * Returns the item body content
     * for the given item body
     * @param {} $itemBody - The item body to
     * retreive the body content from
     */
    static _getItemBodyContent($itemBody) {
        return $itemBody.find(config.selectors.itemBodyContent);
    }
}

moduleRegistry.registerModule('Accordion', Accordion);
moduleRegistry.autoApplyModule('Accordion', config.selectors.module);
